import { ReactNode } from "react";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { Content, Header, Item, Root, Trigger } from "@radix-ui/react-accordion";

import UIcon from "lib/icons/untitled_ui/UIcon";
import { Flex, Text } from "../../../styles/utility-components";

type TriggerStyles = {
  padding?: string;
  margin?: string;
};

export type CollapseItem = {
  header: ReactNode;
  content: ReactNode;
  key: string;
  chevronText?: string;
};

interface CollapseGroupProps {
  items: CollapseItem[];
  value?: string[];
  onChange?: (value: string[]) => void;
  defaultValue?: string[];
  showDivider?: boolean;
  showFullBorder?: boolean;
  triggerStyles?: TriggerStyles;
}

function CollapseGroup({
  items,
  value,
  onChange,
  defaultValue,
  triggerStyles,
  showDivider = true,
  showFullBorder = false,
}: CollapseGroupProps) {
  return (
    <Root type="multiple" value={value} onValueChange={onChange} defaultValue={defaultValue}>
      {items &&
        items.map((item) => (
          <StyledItem
            key={item.key}
            value={item.key}
            borderBottom={showDivider}
            fullBorder={showFullBorder}
          >
            <StyledHeader>
              <StyledTrigger {...triggerStyles}>
                {item.header}
                <Flex alignItems="center" gap={8}>
                  {item.chevronText && <Subtitle description>{item.chevronText}</Subtitle>}
                  <ChevronIcon icon="chevronDown" size={16} color="sysTextPlaceholder" />
                </Flex>
              </StyledTrigger>
            </StyledHeader>
            <StyledContent>{item.content}</StyledContent>
          </StyledItem>
        ))}
    </Root>
  );
}

export default CollapseGroup;

const StyledItem = styled(Item, {
  shouldForwardProp: (prop) => !["borderBottom", "fullBorder"].includes(prop as string),
})<{
  borderBottom?: boolean;
  fullBorder?: boolean;
}>(({ theme, borderBottom, fullBorder }) => ({
  // Apply side borders when fullBorder is enabled
  borderLeft: fullBorder ? `1px solid ${theme.colors.sysBorderDecorative}` : undefined,
  borderRight: fullBorder ? `1px solid ${theme.colors.sysBorderDecorative}` : undefined,

  // Apply top border only to first item when fullBorder is enabled
  "&:first-of-type": {
    borderTop: fullBorder ? `1px solid ${theme.colors.sysBorderDecorative}` : undefined,
  },

  // Apply bottom border to all items when borderBottom is true
  // BUT, if fullBorder is true, exclude the last item (which gets its own rule)
  borderBottom: borderBottom ? `1px solid ${theme.colors.sysBorderDecorative}` : undefined,

  "&:last-of-type": {
    borderBottom: fullBorder ? `1px solid ${theme.colors.sysBorderDecorative}` : undefined,
    // Remove the duplicate border from the general rule when both flags are true
    ...(borderBottom &&
      fullBorder && {
        borderBottomWidth: "1px", // Ensure only single border width
      }),
  },
}));

const ChevronIcon = styled(UIcon)({
  transition: "transform 300ms cubic-bezier(0.87, 0, 0.13, 1)",
  '[data-state="open"] &': {
    transform: "rotate(180deg)",
  },
});

const StyledTrigger = styled(Trigger)<{ padding?: string; margin?: string }>(
  ({ theme, padding, margin }) => ({
    appearance: "none",
    background: "none",
    border: "none",
    margin: margin ?? 0,
    font: "inherit",
    color: "inherit",
    textAlign: "inherit",
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center",
    padding: padding ?? "24px",
    fontSize: "14px",
    cursor: "pointer",
    transition: "background-color 0.3s ease",

    "&:hover": {
      backgroundColor: theme.colors.sysBackgroundAlternative,
    },
  }),
);

const StyledHeader = styled(Header)({
  display: "flex",
  width: "100%",
  margin: 0,
});

const Subtitle = styled(Text)<{ disabled?: boolean }>(({ disabled, theme }) => ({
  color: disabled ? theme.colors.sysTextInactive : theme.colors.sysTextSecondary,
}));

const slideDown = keyframes({
  from: {
    height: "0",
  },
  to: {
    height: "var(--radix-accordion-content-height)",
  },
});

const slideUp = keyframes({
  from: {
    height: "var(--radix-accordion-content-height)",
  },
  to: {
    height: "0",
  },
});

const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

// Fade fully out over the first half of the collapse, then hold at 0 for the
// rest so the content can't pop back in while the height is still shrinking.
const fadeOut = keyframes({
  "0%": { opacity: 1 },
  "50%": { opacity: 0 },
  "100%": { opacity: 0 },
});

const StyledContent = styled(Content)({
  "&[data-state='open']": {
    animation: `${slideDown} 300ms cubic-bezier(0.87, 0, 0.13, 1), ${fadeIn} 200ms ease-out`,
  },

  // On close, fade the content out over the first half of the collapse and keep
  // it hidden for the rest (fadeOut runs the full 300ms and holds at 0), and
  // clip overflow so nothing spills over the content below mid-animation.
  "&[data-state='closed']": {
    overflow: "hidden",
    animation: `${slideUp} 300ms cubic-bezier(0.87, 0, 0.13, 1), ${fadeOut} 300ms ease-out`,
  },

  // Reduced motion: drop the height slide (movement) but keep the opacity fade
  // so the transition still reads without animating position.
  "@media (prefers-reduced-motion: reduce)": {
    "&[data-state='open']": {
      animation: `${fadeIn} 200ms ease-out`,
    },
    "&[data-state='closed']": {
      animation: `${fadeOut} 300ms ease-out`,
    },
  },
});
